import type { FavoriteTargetType } from "@compass/shared/schema/favorite";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { TrendingUp } from "lucide-react";
import { trendingRoute } from "app/router";
import { SocialPostWrapper } from "screens/social/post/SocialPostWrapper";
import { Spinner } from "components/elements";
import { DigestCard } from "components/elements/DigestCard";
import { EventCard } from "components/elements/EventCard";
import { VendorCard } from "components/elements/VendorCard";
import { trpc } from "lib/trpc";
import { BlogPostPreview } from "screens/blog/BlogPostPreview";
import { RssFeedCard } from "screens/rss/RssFeedCard";
import type { ClientPost } from "@compass/shared/types";

const TYPE_FILTERS: { label: string; value: FavoriteTargetType }[] = [
    { label: "Posts", value: "post" },
    { label: "Articles", value: "feedArticle" },
    { label: "Blog Posts", value: "blogPost" },
    { label: "Events", value: "event" },
    { label: "Vendors", value: "vendor" },
    { label: "AI Digests", value: "sitewideSummary" },
];

const TrendingBadge = ({ rank, score }: { rank: number; score: number }) => (
    <div className="absolute top-2 left-2 z-10 flex items-center gap-1.5 rounded-full bg-orange-500 px-2.5 py-1 shadow-md">
        <span className="text-xs font-bold text-white">#{rank}</span>
        <span className="text-[10px] text-orange-100">Score {score}</span>
    </div>
);

export const Trending = () => {
    const search = useSearch({ from: trendingRoute.id });
    const navigate = useNavigate();

    const selectedTypes = new Set<string>(
        search.types ? search.types.split(",").filter(Boolean) : [],
    );
    const hasFilters = selectedTypes.size > 0;

    // API supports single filterType — if one selected, pass to API; otherwise fetch all and filter client-side
    const apiFilterType =
        selectedTypes.size === 1 ? (Array.from(selectedTypes)[0] as FavoriteTargetType) : undefined;

    const { data, isLoading, isError } = trpc.favorites.getTrending.useQuery({
        limit: 50,
        sinceDays: 7,
        filterType: apiFilterType,
    });

    const filteredData =
        data && selectedTypes.size > 1 ? data.filter((item) => selectedTypes.has(item.type)) : data;

    const toggleType = (value: FavoriteTargetType) => {
        const next = new Set(selectedTypes);
        if (next.has(value)) {
            next.delete(value);
        } else {
            next.add(value);
        }
        const typesParam = next.size > 0 ? Array.from(next).join(",") : undefined;
        navigate({
            search: { types: typesParam },
            replace: true,
        });
    };

    const clearFilters = () => {
        navigate({ search: {}, replace: true });
    };

    return (
        <div className="mx-auto w-full max-w-7xl px-6 pt-6 pb-12">
            <div className="mb-6 flex items-center gap-3">
                <TrendingUp className="h-7 w-7 text-orange-500" />
                <h1 className="font-sans text-[28px] font-semibold text-[#424242]">
                    Trending in Compass
                </h1>
            </div>

            <div className="mb-2 flex flex-wrap gap-1">
                {TYPE_FILTERS.map((opt) => (
                    <button
                        key={opt.value}
                        onClick={() => toggleType(opt.value)}
                        className={`rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
                            selectedTypes.has(opt.value)
                                ? "bg-orange-500 text-white"
                                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                        }`}
                    >
                        {opt.label}
                    </button>
                ))}
            </div>
            {hasFilters && (
                <div className="mb-4 text-right">
                    <button
                        onClick={clearFilters}
                        className="text-xs text-gray-400 hover:text-gray-600"
                    >
                        Clear filters
                    </button>
                </div>
            )}
            {!hasFilters && <div className="mb-4" />}

            {isLoading ? (
                <div className="flex justify-center py-16">
                    <Spinner className="h-8 w-8 text-gray-400" />
                </div>
            ) : isError ? (
                <div className="py-16 text-center text-red-500">Failed to load trending data.</div>
            ) : !filteredData || filteredData.length === 0 ? (
                <div className="py-16 text-center">
                    <TrendingUp className="mx-auto mb-4 h-12 w-12 text-gray-300" />
                    <p className="text-lg text-gray-500">Nothing trending yet</p>
                    <p className="mt-1 text-sm text-gray-400">
                        Content becomes trending as users star their favorites.
                    </p>
                </div>
            ) : (
                <div className="columns-1 gap-4 space-y-4 sm:columns-2 lg:columns-3 xl:columns-4">
                    {filteredData.map((item, index) => (
                        <div
                            key={`${item.type}:${item.data.id}`}
                            className="relative break-inside-avoid"
                        >
                            <TrendingBadge rank={index + 1} score={item.score} />
                            {item.type === "post" && (
                                <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white p-4 pt-10 shadow-sm">
                                    <SocialPostWrapper
                                        post={item.data as ClientPost}
                                        compact
                                        target="_blank"
                                    />
                                </div>
                            )}
                            {item.type === "feedArticle" && (
                                <div className="pt-4">
                                    <RssFeedCard
                                        title={item.data.title}
                                        description={item.data.excerpt}
                                        date={item.data.publishedAt}
                                        link={item.data.url}
                                        imageUrl={item.data.imageUrl}
                                        feedSourceTitle={item.data.feedSourceTitle}
                                        articleId={item.data.id}
                                    />
                                </div>
                            )}
                            {item.type === "blogPost" && (
                                <div className="pt-4">
                                    <Link
                                        to="/p/$slug"
                                        params={{ slug: item.data.slug }}
                                        target="_blank"
                                        className="block no-underline"
                                    >
                                        <BlogPostPreview post={item.data} />
                                    </Link>
                                </div>
                            )}
                            {item.type === "event" && (
                                <div className="pt-4">
                                    <EventCard event={item.data} target="_blank" />
                                </div>
                            )}
                            {item.type === "vendor" && (
                                <div className="pt-4">
                                    <VendorCard vendor={item.data} target="_blank" />
                                </div>
                            )}
                            {item.type === "sitewideSummary" && (
                                <div className="pt-4">
                                    <DigestCard summary={item.data} target="_blank" />
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
